import React, { useState, useEffect } from 'react';
import {
  Clock,
  Play,
  Pause,
  Square,
  Calendar,
  Plus,
  Edit,
  Trash2,
  Timer,
  BarChart3,
  Download,
  Filter,
  Search,
  X,
  CheckCircle
} from 'lucide-react';
import { useAuthStore, api } from '../auth/authStore';
import { UserDashboardLayout } from './UserDashboardLayout';
import { EnhancedTimeTracker } from '../../components/TimeTracker';
import { useTimeTracker } from '../../hooks/useTimeTracker';
import toast from 'react-hot-toast';

interface TimeEntry {
  id: string;
  taskId: string;
  taskTitle: string;
  clientName: string;
  startTime: string;
  endTime?: string;
  duration: number; // in minutes
  description?: string;
  date: string;
  isRunning: boolean;
}

interface Task {
  id: string;
  title: string;
  description?: string;
  clientName?: string;
  clientId?: string;
  status: string;
  priority: string;
  dueDate: string;
}



interface TimeStats {
  todayHours: number;
  weekHours: number;
  monthHours: number;
  billableHours: number;
}

const formatTime = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${mins}m`;
};

const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Add/Edit Time Entry Modal Component
const TimeEntryModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSave: (entry: Partial<TimeEntry>) => void;
  entry?: TimeEntry | null;
  mode: 'add' | 'edit';
}> = ({ isOpen, onClose, onSave, entry, mode }) => {
  const [formData, setFormData] = useState({
    taskId: '',
    taskTitle: '',
    clientName: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    startTime: '',
    endTime: '',
    duration: 0,
  });
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchTasks();
      if (mode === 'edit' && entry) {
        const startDate = new Date(entry.startTime);
        const endDate = entry.endTime ? new Date(entry.endTime) : null;
        setFormData({
          taskId: entry.taskId,
          taskTitle: entry.taskTitle,
          clientName: entry.clientName,
          description: entry.description || '',
          date: entry.date,
          startTime: startDate.toTimeString().slice(0, 5),
          endTime: endDate ? endDate.toTimeString().slice(0, 5) : '',
          duration: entry.duration,
        });
      } else {
        // Reset form for add mode
        setFormData({
          taskId: '',
          taskTitle: '',
          clientName: '',
          description: '',
          date: new Date().toISOString().split('T')[0],
          startTime: '',
          endTime: '',
          duration: 0,
        });
      }
    }
  }, [isOpen, mode, entry]);

  const fetchTasks = async () => {
    try {
      console.log('Fetching tasks for time entry modal...');

      // Try multiple endpoints to get tasks
      let response;
      try {
        response = await api.get('/tasks/my-tasks');
      } catch (myTasksError) {
        console.log('my-tasks endpoint failed, trying assigned...');
        try {
          response = await api.get('/tasks/assigned');
        } catch (assignedError) {
          console.log('assigned endpoint failed, trying workflow...');
          response = await api.get('/tasks/workflow');
        }
      }

      console.log('Tasks response for modal:', response.data);
      if (response.data.success) {
        setTasks(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching tasks for modal:', error);
    }
  };

  const handleTaskChange = (taskId: string) => {
    const selectedTask = tasks.find(t => t.id === taskId);
    if (selectedTask) {
      setFormData(prev => ({
        ...prev,
        taskId,
        taskTitle: selectedTask.title,
        clientName: selectedTask.clientName || '',
      }));
    }
  };

  const calculateDuration = () => {
    if (formData.startTime && formData.endTime) {
      const start = new Date(`${formData.date}T${formData.startTime}`);
      const end = new Date(`${formData.date}T${formData.endTime}`);
      const diffMinutes = Math.floor((end.getTime() - start.getTime()) / (1000 * 60));
      setFormData(prev => ({ ...prev, duration: diffMinutes }));
    }
  };

  useEffect(() => {
    calculateDuration();
  }, [formData.startTime, formData.endTime, formData.date]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.taskId || !formData.startTime) {
      toast.error('Please select a task and enter start time');
      return;
    }

    const startDateTime = new Date(`${formData.date}T${formData.startTime}`);
    const endDateTime = formData.endTime ? new Date(`${formData.date}T${formData.endTime}`) : undefined;

    const timeEntry: Partial<TimeEntry> = {
      id: entry?.id,
      taskId: formData.taskId,
      taskTitle: formData.taskTitle,
      clientName: formData.clientName,
      description: formData.description,
      date: formData.date,
      startTime: startDateTime.toISOString(),
      endTime: endDateTime?.toISOString(),
      duration: formData.duration,
      isRunning: false,
    };

    onSave(timeEntry);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            {mode === 'add' ? 'Add Time Entry' : 'Edit Time Entry'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Task *
              </label>
              <select
                value={formData.taskId}
                onChange={(e) => handleTaskChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              >
                <option value="">Select a task</option>
                {tasks.map((task) => (
                  <option key={task.id} value={task.id}>
                    {task.title} {task.clientName && `(${task.clientName})`}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date *
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Time *
              </label>
              <input
                type="time"
                value={formData.startTime}
                onChange={(e) => setFormData(prev => ({ ...prev, startTime: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Time
              </label>
              <input
                type="time"
                value={formData.endTime}
                onChange={(e) => setFormData(prev => ({ ...prev, endTime: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Duration (minutes)
              </label>
              <input
                type="number"
                value={formData.duration}
                onChange={(e) => setFormData(prev => ({ ...prev, duration: parseInt(e.target.value) || 0 }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                min="0"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Add notes about your work..."
            />
          </div>

          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              {mode === 'add' ? 'Add Entry' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const TimeEntryRow: React.FC<{
  entry: TimeEntry;
  onEdit: (entry: TimeEntry) => void;
  onDelete: (entryId: string) => void;
}> = ({ entry, onEdit, onDelete }) => {
  if (!entry) return null;

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm font-medium text-gray-900">{entry.taskTitle || '-'}</div>
        <div className="text-sm text-gray-500">{entry.clientName || '-'}</div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        {entry.date ? new Date(entry.date).toLocaleDateString() : '-'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        {entry.startTime ? new Date(entry.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        {entry.endTime ? new Date(entry.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        {entry.duration ? formatTime(entry.duration) : '0:00'}
      </td>
      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
        {entry.description || '-'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => onEdit(entry)}
            className="text-primary-600 hover:text-primary-900"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(entry.id)}
            className="text-red-600 hover:text-red-900"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
};

// Wrapper component to handle authentication check
const UserTimesheetWrapper: React.FC = () => {
  const { user, isAuthenticated, accessToken, isLoading } = useAuthStore();

  // Debug logging
  console.log('UserTimesheetWrapper - Auth State:', {
    user: user ? { id: user.id, email: user.email, roles: user.roles } : null,
    isAuthenticated,
    hasAccessToken: !!accessToken,
    isLoading
  });

  // Show loading if auth is still loading or user data is not available
  if (isLoading || !user || !isAuthenticated || !accessToken) {
    console.log('UserTimesheetWrapper - Showing loading state');
    return (
      <UserDashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <p className="ml-3 text-gray-600">
            {isLoading ? 'Loading...' : 'Authenticating...'}
          </p>
        </div>
      </UserDashboardLayout>
    );
  }

  console.log('UserTimesheetWrapper - Rendering content');
  return <UserTimesheetContent />;
};

const UserTimesheetContent: React.FC = () => {
  const { user, isAuthenticated, accessToken, tenant, refreshAccessToken, logout } = useAuthStore();
  // const timeTracker = useTimeTracker(user?.id); // Unused for now
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [stats, setStats] = useState<TimeStats>({
    todayHours: 0,
    weekHours: 0,
    monthHours: 0,
    billableHours: 0,
  });
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [showEditEntry, setShowEditEntry] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);

  useEffect(() => {
    if (user && isAuthenticated && accessToken) {
      fetchTimeEntries();
    }
  }, [selectedDate, user, isAuthenticated, accessToken]);

  useEffect(() => {
    if (user && isAuthenticated) {
      calculateStats();
    }
  }, [timeEntries, user, isAuthenticated]);



  const fetchTimeEntries = async () => {
    if (!user || !isAuthenticated || !accessToken) {
      console.log('Skipping fetchTimeEntries - user not authenticated');
      return;
    }

    try {
      setLoading(true);
      console.log('Fetching time entries for date:', selectedDate);
      console.log('User:', user);
      console.log('API base URL:', api.defaults.baseURL);
      console.log('Auth store state:', {
        isAuthenticated,
        accessToken: accessToken ? 'Present' : 'Missing',
        tenant: tenant?.subdomain || 'No firm'
      });
      console.log('API headers:', api.defaults.headers.common);

      // Add cache-busting parameter to ensure fresh data
      const cacheBuster = Date.now();
      const response = await api.get(`/time-entries/my-entries?date=${selectedDate}&_t=${cacheBuster}`);
      console.log('Time entries response:', response.data);

      if (response.data.success) {
        const entries = response.data.data || [];
        console.log('✅ Time entries loaded:', entries.length, 'entries');
        console.log('📋 Sample entry:', entries[0]);
        setTimeEntries(entries);
      } else {
        console.error('API returned success: false', response.data);
        toast.error(response.data.message || 'Failed to load time entries');
      }
    } catch (error: any) {
      console.error('Error fetching time entries:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Request headers:', error.config?.headers);

      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.');
        logout();
        window.location.href = '/login';
      } else if (error.response?.status === 403) {
        toast.error('Access denied. Please check your permissions.');
      } else {
        toast.error(error.response?.data?.message || 'Failed to load time entries');
      }
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = () => {
    const today = new Date().toDateString();
    const thisWeekStart = new Date();
    thisWeekStart.setDate(thisWeekStart.getDate() - thisWeekStart.getDay());
    const thisMonthStart = new Date();
    thisMonthStart.setDate(1);

    const todayEntries = timeEntries.filter(entry => 
      new Date(entry.date).toDateString() === today
    );
    const weekEntries = timeEntries.filter(entry => 
      new Date(entry.date) >= thisWeekStart
    );
    const monthEntries = timeEntries.filter(entry => 
      new Date(entry.date) >= thisMonthStart
    );

    setStats({
      todayHours: todayEntries.reduce((sum, entry) => sum + entry.duration, 0) / 60,
      weekHours: weekEntries.reduce((sum, entry) => sum + entry.duration, 0) / 60,
      monthHours: monthEntries.reduce((sum, entry) => sum + entry.duration, 0) / 60,
      billableHours: monthEntries.reduce((sum, entry) => sum + entry.duration, 0) / 60, // Assuming all are billable for now
    });
  };



  const handleSaveTimeEntry = async (entryData: Partial<TimeEntry>) => {
    console.log('🔄 Saving time entry:', entryData);

    try {
      let response;

      if (entryData.id) {
        // Update existing entry
        console.log('📝 Updating existing time entry:', entryData.id);
        response = await api.patch(`/time-entries/${entryData.id}`, {
          description: entryData.description,
          startTime: entryData.startTime,
          endTime: entryData.endTime,
          duration: entryData.duration,
        });

        console.log('✅ Update response:', response.data);

        if (response.data.success) {
          toast.success('Time entry updated');
          console.log('🔄 Refreshing time entries after update...');
          await fetchTimeEntries(); // Wait for fetch to complete
          console.log('✅ Time entries refreshed after update');
          setShowEditEntry(false);
          setEditingEntry(null);
        } else {
          throw new Error(response.data.message || 'Failed to update time entry');
        }
      } else {
        // Create new entry
        console.log('➕ Creating new time entry');
        response = await api.post('/time-entries', {
          taskId: entryData.taskId,
          description: entryData.description,
          startTime: entryData.startTime,
          endTime: entryData.endTime,
          duration: entryData.duration,
        });

        console.log('✅ Create response:', response.data);

        if (response.data.success) {
          toast.success('Time entry added');
          console.log('🔄 Refreshing time entries after create...');
          await fetchTimeEntries(); // Wait for fetch to complete
          console.log('✅ Time entries refreshed after create');
          setShowAddEntry(false);
        } else {
          throw new Error(response.data.message || 'Failed to create time entry');
        }
      }
    } catch (error: any) {
      console.error('❌ Error saving time entry:', error);
      console.error('❌ Error response:', error.response?.data);
      const message = error.response?.data?.message || error.message || 'Failed to save time entry';
      toast.error(message);
    }
  };

  const deleteTimeEntry = async (entryId: string) => {
    if (!confirm('Are you sure you want to delete this time entry?')) return;

    try {
      await api.delete(`/time-entries/${entryId}`);
      toast.success('Time entry deleted');
      await fetchTimeEntries(); // Wait for fetch to complete
    } catch (error) {
      console.error('Error deleting time entry:', error);
      toast.error('Failed to delete time entry');
    }
  };

  const editTimeEntry = (entry: TimeEntry) => {
    setEditingEntry(entry);
    setShowEditEntry(true);
  };

  const exportTimesheet = async () => {
    try {
      const response = await api.get(`/time-entries/my-entries?startDate=${selectedDate}&endDate=${selectedDate}&format=csv`);
      if (response.data.success) {
        // Create and download CSV file
        const csvContent = generateCSV(timeEntries);
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `timesheet-${selectedDate}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('Timesheet exported successfully');
      }
    } catch (error) {
      console.error('Error exporting timesheet:', error);
      toast.error('Failed to export timesheet');
    }
  };

  const generateCSV = (entries: TimeEntry[]): string => {
    const headers = ['Date', 'Task', 'Client', 'Start Time', 'End Time', 'Duration (hours)', 'Description'];
    const rows = entries.filter(entry => entry && entry.date && entry.taskTitle).map(entry => [
      new Date(entry.date).toLocaleDateString(),
      entry.taskTitle || '-',
      entry.clientName || '-',
      entry.startTime ? new Date(entry.startTime).toLocaleTimeString() : '-',
      entry.endTime ? new Date(entry.endTime).toLocaleTimeString() : '-',
      entry.duration ? (entry.duration / 60).toFixed(2) : '0.00',
      entry.description || '-'
    ]);

    return [headers, ...rows].map(row =>
      row.map(field => `"${field}"`).join(',')
    ).join('\n');
  };

  const filteredEntries = timeEntries.filter(entry => {
    if (!entry) return false;

    // If no search term, show all entries
    if (!searchTerm.trim()) return true;

    // Search in available fields
    const searchLower = searchTerm.toLowerCase();
    const taskTitle = entry.taskTitle || '';
    const clientName = entry.clientName || '';
    const description = entry.description || '';

    return (
      taskTitle.toLowerCase().includes(searchLower) ||
      clientName.toLowerCase().includes(searchLower) ||
      description.toLowerCase().includes(searchLower)
    );
  });

  // Debug logging
  console.log('🔍 Filtering entries:', {
    totalEntries: timeEntries.length,
    filteredEntries: filteredEntries.length,
    searchTerm,
    sampleEntry: timeEntries[0]
  });

  // Show loading while fetching data
  if (loading) {
    return (
      <UserDashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </UserDashboardLayout>
    );
  }

  return (
    <UserDashboardLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Timesheet</h2>
          <p className="text-gray-600">
            Track your time and manage your work hours.
          </p>
        </div>

        {/* Timer Section */}
        <div className="mb-8">
          <EnhancedTimeTracker
            userRole={user?.roles?.[0] || 'Staff'}
            userId={user?.id || ''}
            onTimeEntryCreated={async () => {
              await fetchTimeEntries();
              calculateStats();
            }}
            onTimerStarted={async () => {
              await fetchTimeEntries();
            }}
            onTimerStopped={async () => {
              await fetchTimeEntries();
              calculateStats();
            }}
          />
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Today</p>
                <p className="text-2xl font-bold text-gray-900">{stats.todayHours.toFixed(1)}h</p>
              </div>
              <Clock className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">This Week</p>
                <p className="text-2xl font-bold text-gray-900">{stats.weekHours.toFixed(1)}h</p>
              </div>
              <Calendar className="w-8 h-8 text-green-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">This Month</p>
                <p className="text-2xl font-bold text-gray-900">{stats.monthHours.toFixed(1)}h</p>
              </div>
              <BarChart3 className="w-8 h-8 text-purple-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Billable</p>
                <p className="text-2xl font-bold text-gray-900">{stats.billableHours.toFixed(1)}h</p>
              </div>
              <Timer className="w-8 h-8 text-orange-500" />
            </div>
          </div>
        </div>

        {/* Filters and Controls */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search entries..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={exportTimesheet}
                className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </button>
              <button
                onClick={() => setShowAddEntry(true)}
                className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Entry
              </button>
            </div>
          </div>
        </div>

        {/* Time Entries Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Time Entries</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Task
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Start Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    End Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredEntries.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center">
                      <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No time entries found</h3>
                      <p className="text-gray-600">
                        {searchTerm ? 'Try adjusting your search.' : 'Start tracking time on your tasks.'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredEntries.map((entry) => (
                    <TimeEntryRow
                      key={entry.id}
                      entry={entry}
                      onEdit={editTimeEntry}
                      onDelete={deleteTimeEntry}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>



        {/* Add Time Entry Modal */}
        <TimeEntryModal
          isOpen={showAddEntry}
          onClose={() => setShowAddEntry(false)}
          onSave={handleSaveTimeEntry}
          mode="add"
        />

        {/* Edit Time Entry Modal */}
        <TimeEntryModal
          isOpen={showEditEntry}
          onClose={() => {
            setShowEditEntry(false);
            setEditingEntry(null);
          }}
          onSave={handleSaveTimeEntry}
          entry={editingEntry}
          mode="edit"
        />
      </div>
    </UserDashboardLayout>
  );
};

// Export the wrapper component as the main export
export const UserTimesheet = UserTimesheetWrapper;
